import { useWindowSize } from '@vueuse/core'
import { MenuItemType } from '~/enums/menu-item'
import type { Image, MenuItem } from '~/types'
import { useAppStore } from '~/store/useAppStore'

const appStore = useAppStore()
const { mainMenuClickHandler } = appStore

export interface MenuIdState {
  menuId: Maybe<number>
}

export const useMenuItem = (menuItem: MaybeRef<MenuItem.Model>, isTopLevel?: boolean) => {
  const componentsHeader = {
    'text': 'MenuTextMenuItem',
    'column': 'MenuColumnMenuItem',
    'static-url': 'MenuStaticUrlMenuItem',
    'menu-item-with-icon': 'MenuIconMenuItem',
  }

  const children = computed<MenuItem.Model[]>(() => unref(menuItem).children || [])
  const hasSubMenu = computed<boolean>(() => children.value.length > 0)
  const name = computed<Maybe<string>>(() => unref(menuItem).name || null)
  const type = computed<MenuItem.MenuItemType>(() => unref(menuItem).type)
  const target = computed<Maybe<string>>(() => unref(menuItem).target || null)
  const icon = computed<Maybe<Image>>(() => unref(menuItem)?.data?.iconData || null)
  const value = computed<Maybe<string>>(() => unref(menuItem).value || null)
  const menuItemId = computed<Maybe<number>>(() => unref(menuItem).id || null)
  const componentForMenuItem = computed<string>(() => componentsHeader[type.value])

  const isStaticUrlMenuItem = computed<boolean>(() => type.value === MenuItemType.StaticUrl)
  const isTextMenuItem = computed<boolean>(() => type.value === MenuItemType.Text)
  const isColumnMenuItem = computed<boolean>(() => type.value === MenuItemType.Column)

  const menuItemOpened = useState<MenuIdState>('menu-item', () => ({ menuId: null }))
  const topMenuOpened = useState<MenuIdState>('top-menu-item', () => ({ menuId: null }))
  const isOpen = computed<boolean>(() => menuItemOpened.value.menuId === menuItemId.value)
  const isTopMenuOpened = computed<boolean>(() => topMenuOpened.value.menuId === menuItemId.value)
  const isMainMenuOpened = useState<boolean>('is-main-menu-opened')
  const isTeamMenuOpened = useState<boolean>('is-team-menu-opened')

  const { width } = useWindowSize({ initialWidth: 0 })
  const isSmallScreen = computed<boolean>(() => width.value < 1280)

  const setTopMenuId = (id: Maybe<number>): void => {
    if (topMenuOpened.value.menuId === id) {
      topMenuOpened.value.menuId = null
      return
    }
    topMenuOpened.value.menuId = id
  }

  const setMenuId = (id: Maybe<number>): void => {
    menuItemOpened.value.menuId = id
  }

  const handleClickOnTopMenu = (): void => {
    if (!hasSubMenu.value) {
      mainMenuClickHandler()
      isTeamMenuOpened.value = false
    }
    else if (!isTopLevel) {
      return
    }
    setMenuId(null)
    setTopMenuId(menuItemId.value)
  }

  const handleClick = (): void => {
    if (isTextMenuItem.value) {
      return
    }
    isMainMenuOpened.value = false
    setMenuId(null)
    setTopMenuId(null)
  }

  const handleMouseLeave = (): void => {
    if (topMenuOpened.value.menuId || isSmallScreen.value) {
      return
    }
    setMenuId(null)
  }

  const handleMouseOver = (): void => {
    if (topMenuOpened.value.menuId || isSmallScreen.value) {
      return
    }
    setMenuId(menuItemId.value)
  }

  watch(isMainMenuOpened, (value) => {
    if (value) {
      return
    }
    setMenuId(null)
  })

  return {
    children,
    componentForMenuItem,
    hasSubMenu,
    icon,
    isColumnMenuItem,
    isMainMenuOpened,
    isOpen,
    isStaticUrlMenuItem,
    isTextMenuItem,
    isTopMenuOpened,
    menuItemId,
    name,
    target,
    value,

    handleClick,
    handleClickOnTopMenu,
    handleMouseLeave,
    handleMouseOver,
    setMenuId,
    setTopMenuId,
  }
}
